import { type BackgroundProps, HStack, Text } from '@chakra-ui/react'
import { faHourglassEnd } from '@fortawesome/pro-regular-svg-icons'
import { faDoorClosed } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'

import { pluralize } from '@/utils/helpers'

import SeriesQ from '@/queries/SeriesQ'
import WorldQ from '@/queries/WorldQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import { SERIES_CHATS_CLOSE_AFTER_DAYS } from '@/client/services/series/constants'
import { getDashboardProgramsUrl, getDashboardSessionsUrl } from '@/client/services/url'
import { isProgramWorld } from '@/client/services/worlds/helpers'

import ShwLink from '@/components/ShwLink'

type BannerProps = {
  bgColor: BackgroundProps['backgroundColor']
  icon: FontAwesomeIconProps['icon']
  body: string | (() => JSX.Element)
}

function Banner({ bgColor, icon, body }: BannerProps): JSX.Element {
  return (
    <HStack borderRadius="8px" p="24px" backgroundColor={bgColor} spacing={4}>
      <FontAwesomeIcon icon={icon} />
      {typeof body === 'string' ? <Text textAlign="left">{body}</Text> : body()}
    </HStack>
  )
}

export default function SeriesBannerEnrollmentStatus({
  series,
}: {
  series: Query
}): JSX.Element | null {
  const seriesEndDate = DateTime.fromISO(`${series(SeriesQ.closed_at)}`)

  const user = useUser()
  const { requestTimestamp } = useAppContext()
  const now = DateTime.fromISO(requestTimestamp)
  const hasSeriesEnded = now >= seriesEndDate
  const seriesClosedReason = series(SeriesQ.closed_reason)
  const isEnrollmentClosed = series(SeriesQ.isEnrollmentClosed()) ?? true
  const registeredUsers = series(SeriesQ.countRegistrations()) ?? 0
  const spotsLeft = Math.max((series(SeriesQ.max_learners) ?? 0) - registeredUsers, 0)
  const isRegisteredForSession = series(SeriesQ.isUserRegistered(user.id)) ?? false
  const isHost = series(SeriesQ.isHost({ userId: user.id })) ?? false

  const world = series.one(SeriesQ.getWorld())
  const worldTitle = world(WorldQ.title)
  const worldSlug = world(WorldQ.slug) ?? ''

  if (hasSeriesEnded) {
    const chatsClosedNotice = `All 1:1 and group chats related to this series are disabled ${pluralize(
      SERIES_CHATS_CLOSE_AFTER_DAYS,
      'day'
    )} after the last session.`
    if (seriesClosedReason != null) {
      return (
        <Banner
          bgColor="warning.subtle"
          icon={faHourglassEnd}
          body={() => (
            <Text textAlign="left">
              This series was cancelled {seriesClosedReason === 'tutor' ? 'by the tutor ' : ''}on{' '}
              {seriesEndDate.toLocaleString(DateTime.DATE_FULL)}. We&apos;re very sorry–you can
              explore more {worldTitle} series{' '}
              <ShwLink
                href={
                  isProgramWorld(worldSlug)
                    ? getDashboardProgramsUrl({
                        programSlug: PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug],
                      })
                    : getDashboardSessionsUrl({ worldSlug })
                }
              >
                here
              </ShwLink>
              . {chatsClosedNotice}
            </Text>
          )}
        />
      )
    }

    return (
      <Banner
        bgColor="bg.muted"
        icon={faHourglassEnd}
        body={`This series ended on ${seriesEndDate.toLocaleString(
          DateTime.DATE_FULL
        )}. ${chatsClosedNotice}`}
      />
    )
  }

  if (!isRegisteredForSession && !isHost && isEnrollmentClosed) {
    return (
      <Banner
        bgColor="bg.muted"
        icon={faDoorClosed}
        body="Enrollment for this series has closed."
      />
    )
  }

  if (!isRegisteredForSession && !isHost && spotsLeft === 0) {
    return (
      <Banner
        bgColor="bg.muted"
        icon={faDoorClosed}
        body="This series is full. Spots may open up, so try checking back later!"
      />
    )
  }

  return null
}
